<template>
  <div>
    <LvText
      v-if="title"
      h6
      bold
      class="mb-8"
    >
      {{ $t(title) }}<LvText v-if="required" red>*</LvText>
    </LvText>
    <jodit-vue v-model="text" :buttons="buttons" :config="config"/>
    <ValidationProvider
      v-slot="{ errors }"
      :rules="rules"
    >
      <input
        type="hidden"
        :value="value"
      >
      <div
        v-if="errors.length > 0"
        class="invalid-text"
      >
        <i class="icon-warning invalid-icon" />
        <p
          v-for="(text_e,text_k) in errors"
          :key="text_k"
          class="text"
        >
          {{ $t(text_e) }}
        </p>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import {JoditVue} from 'jodit-vue';
import 'jodit/build/jodit.min.css'
import Spare_wysiwygEditor from '@/spare/wysiwygEditor';

// 與 app.scss 的網站字型堆疊一致，匯出 HTML 時後端需要每個文字元素帶 font-family
const FONT_FAMILY = "'Lato', 'PingFangTC', 'Helvetica Neue', 'Microsoft JhengHei', Helvetica, Arial, sans-serif";
// 新段落與貼上內容的預設 style；margin 明確寫 0，後端轉 Word 才不會套預設段距
const getPStyle = fontSize =>
  `line-height: 1.78;font-size: ${fontSize};letter-spacing: 0.8px;margin-top: 0;margin-bottom: 0;`;
// 把 % 或 px 的寬度換成以 base 為基準的整數 px，沒有寬度就視為填滿 base
const toPx = (width, base) => {
  if (width.endsWith('px')) return Math.round(parseFloat(width));
  if (width.endsWith('%')) return Math.round((base * parseFloat(width)) / 100);
  return base;
};

export default {
  components: { JoditVue },
  props: {
    value: {
      type: String,
    },
    title: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: String,
    },
    // 後端匯出用：補每個文字元素的 font-family、表格 td 的 width 與 tr 的 height
    exportStyle: {
      type: Boolean,
      default: false,
    },
    // Jodit 工具列按鈕，預設用 spare 的完整清單
    buttons: {
      type: Array,
      default: () => Spare_wysiwygEditor.buttons,
    },
    // 匯出用內容區寬度（px），也是表格寬度換算基準；由範本的可編輯區寬度決定
    contentWidth: {
      type: Number,
      default: 601,
    },
    // 新段落與貼上內容的預設字級
    fontSize: {
      type: String,
      default: '1.125rem',
    },
  },
  data() {
    return {
      isModifying: false,
      text: '',
      config: {
        cleanHTML: {
          removeEmptyElements: true,
          fillEmptyParagraph: false,
          replaceNBSP: true,
          removeOnError: true,
        },
        // 關閉會從 cdnjs 動態載入腳本的功能（ace 原始碼編輯器、js-beautify），避免被 CSP script-src 擋下
        beautifyHTML: false,
        sourceEditor: 'area',
        processPasteHTML: true,
        askBeforePasteHTML: false,
        scrollToPastedContent: true,
        events: {
          processPaste: ($event, html) => {
            const emptyTagPattern = /<(\w+)[^>]*>\s*<\/\1>/g;
            const removeStylePattern = /<style\b[^>]*>[\s\S]*?<\/style>/g;
            return html
              .replace(/<p[^>]*>/g, `<p style="${getPStyle(this.fontSize)}">`)
              .replace(
                /<span[^>]*>/g,
                `<span  style="line-height: 1.78;font-size: ${this.fontSize};letter-spacing: 0.8px;">`,
              )
              .replace(/<div[^>]*>/g, '<div>')
              .replace(/<ol[^>]*>/g, '<div>')
              .replace(/<li[^>]*>/g, '<div>')
              .replace(/<title[^>]*>/g, '<p>')
              .replace(/<\/title>/g, '</p>')
              .replace(removeStylePattern, '')
              .replace(emptyTagPattern, '')
              .replace(/\n/g, '');
          },
        },
        createAttributes: {
          table: {
            style:
              'border:1px solid black;border-collapse:collapse;width: 100%;',
          },
          tr: { style: 'border: 1px solid black;' },
          td: { style: 'border: 1px solid black;padding: 8px;' },
          p: { style: getPStyle(this.fontSize) },
        },
        // 匯出用：內容區固定寬並置中，讓折行與列高和 Word 一致
        style: this.exportStyle
          ? { width: `${this.contentWidth}px`, boxSizing: 'content-box', margin: '0 auto' }
          : false,
      },
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.text) this.text = newVal;
    },
    text(newVal) {
      if (this.isModifying) return;
      if (!newVal.trim()) {
        this.$emit('input', newVal);
        return;
      }
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${newVal}</div>`, 'text/html'); // 包裝成 <div> 以處理片段 HTML
      if (!doc.body.firstChild) {
        this.$emit('input', newVal);
        return;
      }
      this.isModifying = true;
      const pTags = doc.querySelectorAll('p');
      pTags.forEach(p => {
        if (p.hasAttribute('style')) {
          // 解析並保持原本的 style，避免覆蓋其他屬性
          const style = p.style;
          if (!style.lineHeight) {
              style.lineHeight = '1.5'; // 只在沒設定 line-height 時才添加
          }
        } else {
            p.setAttribute('style', 'line-height: 1.5;');
        }
      });
      if (this.exportStyle) {
        doc.querySelectorAll('p, span, li, td, th, div, h1, h2, h3, h4, h5, h6').forEach(el => {
          if (!el.style.fontFamily) el.style.fontFamily = FONT_FAMILY;
        });
        // 既有內容的 <p> 補 margin 0，後端轉 Word 才不會套預設段距
        pTags.forEach(p => {
          if (!p.style.marginTop) p.style.marginTop = '0';
          if (!p.style.marginBottom) p.style.marginBottom = '0';
        });
        doc.querySelectorAll('table').forEach(table => {
          // 寬度全部換成內容區寬度下的 px，並同時寫 width 屬性，舊式轉換工具只讀屬性不讀 style
          const tableWidth = toPx(table.style.width, this.contentWidth);
          table.style.width = `${tableWidth}px`;
          table.setAttribute('width', tableWidth);
          // Jodit 拖欄寬只寫在第一列並清掉其他列的 width，所以欄寬以第一列為準套到每一列
          const tableRows = table.querySelectorAll('tr');
          const firstCells = tableRows.length
            ? Array.from(tableRows[0].querySelectorAll('td, th'))
            : [];
          const colWidths = firstCells.map(cell =>
            toPx(cell.style.width || `${100 / firstCells.length}%`, tableWidth),
          );
          tableRows.forEach(tr => {
            const cells = tr.querySelectorAll('td, th');
            cells.forEach((cell, index) => {
              const width =
                cells.length === colWidths.length
                  ? colWidths[index]
                  : Math.round(tableWidth / cells.length);
              // 儲存格寬度含 padding 與框線，和 Jodit 量出的比例、Word 的儲存格寬度定義一致
              cell.style.boxSizing = 'border-box';
              cell.style.width = `${width}px`;
              cell.setAttribute('width', width);
            });
          });
        });
        const liveRows = this.$el.querySelectorAll('.jodit-wysiwyg tr');
        const rows = doc.querySelectorAll('tr');
        // 把編輯器實際渲染的列高寫進 html；初次載入時編輯器尚未渲染，列數不符就略過
        if (liveRows.length === rows.length) {
          rows.forEach((tr, index) => {
            tr.style.height = `${liveRows[index].offsetHeight}px`;
          });
        }
      }
      this.text = doc.body.firstChild.innerHTML;
      this.$emit('input', doc.body.firstChild.innerHTML);
      this.isModifying = false;
    }
  },
  mounted() {
    this.text = this.value;
  },
}
</script>